import { lazy, Suspense, useState } from 'react'
import { LoaderCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { SimpleMarkdown } from '@/frontend/components/SimpleMarkdown'
import MermaidDiagram from '@/frontend/features/Fde/MermaidDiagram'
import RepositoryCodeViewer from '@/frontend/features/Fde/RepositoryCodeViewer'
import type { FdeCopy, RepositoryFile } from '@/frontend/features/Fde/types'

const RepositoryMarkdownEditor = lazy(() => import('@/frontend/features/Fde/RepositoryMarkdownEditor'))
const RepositoryCodeEditor = lazy(() => import('@/frontend/features/Fde/RepositoryCodeEditor'))

interface Props {
  file: RepositoryFile | null
  copy: FdeCopy
  projectId: string
  repositoryRevision: string | null
  editable?: boolean
}

export default function RepositoryFilePreview({ file, copy, projectId, repositoryRevision, editable = false }: Props) {
  const [source, setSource] = useState(editable)
  if (!file) return <div className="flex h-full items-center justify-center p-8 text-sm text-muted-foreground">{copy.material.select_file}</div>
  if (editable && file.preview_type === 'markdown') return <Suspense fallback={<Loading copy={copy} />}><RepositoryMarkdownEditor file={file} projectId={projectId} repositoryRevision={repositoryRevision} copy={copy} /></Suspense>
  if (editable && file.preview_type !== 'mermaid' && file.editable) return <Suspense fallback={<Loading copy={copy} />}><RepositoryCodeEditor file={file} projectId={projectId} repositoryRevision={repositoryRevision} copy={copy} /></Suspense>

  return (
    <section className="flex h-full min-h-0 flex-col overflow-hidden bg-background">
      {file.preview_type === 'mermaid' && <div className="flex h-11 shrink-0 items-center justify-end border-b border-border bg-card px-4"><div className="flex rounded-md border border-border p-0.5"><Button size="sm" variant={!source ? 'secondary' : 'ghost'} onClick={() => setSource(false)}>{copy.material.diagram}</Button><Button size="sm" variant={source ? 'secondary' : 'ghost'} onClick={() => setSource(true)}>{copy.material.source}</Button></div></div>}
      {file.preview_type === 'mermaid' && source && editable ? <Suspense fallback={<Loading copy={copy} />}><RepositoryCodeEditor file={file} projectId={projectId} repositoryRevision={repositoryRevision} copy={copy} /></Suspense> : <div className="min-h-0 flex-1 overflow-auto overscroll-contain [scrollbar-gutter:stable]">
        {file.preview_type === 'unsupported' && <p className="p-8 text-center text-sm text-muted-foreground">{copy.material.unsupported}</p>}
        {file.content != null && file.preview_type === 'text' && <pre className="min-w-max whitespace-pre-wrap p-6 font-mono text-xs leading-6 text-foreground">{file.content}</pre>}
        {file.content != null && file.preview_type === 'code' && <RepositoryCodeViewer file={file} />}
        {file.content != null && file.preview_type === 'markdown' && <div className="mx-auto max-w-3xl px-6 py-10"><SimpleMarkdown content={file.content} /></div>}
        {file.content != null && file.preview_type === 'mermaid' && !source && <div className="p-5"><MermaidDiagram content={file.content} copy={copy} /></div>}
      </div>}
    </section>
  )
}

function Loading({ copy }: { copy: FdeCopy }) {
  return <div className="flex h-full items-center justify-center text-sm text-muted-foreground"><LoaderCircle className="mr-2 size-4 animate-spin" />{copy.material.editor_loading}</div>
}
